/**
 * TicketTrainingTab — Phase 1 + Phase 2 + Phase 3
 *
 * Single job-based recommendation section with contextual keyword scoring
 * and engagement-aware ranking.
 * ticket_jobs → jobs.catalog_job_id → training_artifact_job_map → training_artifacts
 * Phase 2 re-ranks artifacts using ticket notes + job categories + artifact keywords.
 * Phase 3 adds small engagement boost from interaction data.
 */

import { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCompanionReadiness } from '@/hooks/useCompanionReadiness';
import { CompanionInstallModal } from '@/components/training/CompanionInstallModal';
import { MobileTrainingLaunchDialog } from '@/components/training/MobileTrainingLaunchDialog';
import { shouldGateMonitoredLaunch } from '@/lib/trainingCompanion/companionAvailability';
import {
  isMobileLikeRuntime,
  collectPassiveRuntimeSignals,
} from '@/utils/training/companionRuntimeCapability';
import { writeMobileTrainingLaunch } from '@/lib/training/mobileTrainingLaunchState';
import {
  approvedDomainsForUrl,
  isApprovedProviderUrl,
} from '@/lib/trainingCompanion/approvedProviderDomains';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  ExternalLink,
  Loader2,
  SearchX,
  Clock,
  ChevronDown,
  Lock,
  Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Ticket } from '@/types';
import { useJobTrainingArtifacts } from '@/hooks/useJobTrainingArtifacts';
// Batch 18-G8 — Ticket UI consumes the G6 ranked recommendations
// alongside the existing artifact list. We need the G4 contract
// types for badges + reason codes and the clock for `currentDate`.
import type { RankedTrainingRecommendation } from '@/types/trainingRecommendations';
// Batch 18-H2 — ticket-specific evidence display helper. Pure mapping
// from `RankedTrainingRecommendation.candidate.completionSummary` to
// an evidence-badge row. No new query; no F36 composition.
import { buildTicketTrainingEvidenceDisplayState } from '@/utils/training/ticketTrainingEvidenceDisplayState';
// Batch 18-I11 — provider evidence summary hook + pure display helper.
// Provider attestation is rendered as a PARALLEL badge group, NEVER
// merged into the H2 manager/manual evidence row above. The 'ticket'
// surface intentionally hides revoked / pending / superseded /none so
// only attested badges reach the high-density ticket card.
import {
  useTrainingProviderEvidenceSummary,
  type TrainingProviderEvidenceSummaryRow,
} from '@/hooks/useTrainingProviderEvidenceSummary';
import { buildProviderEvidenceDisplayState } from '@/utils/training/providerEvidenceDisplayState';
import { clock } from '@/utils/clock';
import { useOpenTrainingWithSession } from '@/hooks/useOpenTrainingWithSession';
import { RequireFeature } from '@/components/access/RequireFeature';
import { type TicketContext } from '@/utils/trainingRelevanceScorer';
import { type ScoreBreakdown, type EngagementStats, type PensieveStats, hasPensieveBadge } from '@/utils/trainingRelevanceScorer';
// Batch 18-B: ranking-neutral explanation layer. The helper is pure —
// it reads the same `ScoreBreakdown` already produced by
// `useJobTrainingArtifacts` and returns a short English fragment. Does
// not change ranking, filtering, or scoring.
import {
  buildTrainingRecommendationExplanation,
  LEARNED_ENRICHMENT_LABEL,
  RECOMMENDATION_REASON_PREFIX,
} from '@/utils/trainingRecommendationReasons';
import {
  ARTIFACT_TYPE_LABELS,
  ARTIFACT_TYPE_ICONS,
  SKILL_DEPTH_LABELS,
  ACCESS_TYPE_LABELS,
} from '@/types/training';
import type { TrainingArtifact } from '@/types/training';
import { ProviderTrustBadge } from '@/components/training/ProviderTrustBadge';
import { MobileTrainingFallbackLaunchInterstitial } from '@/components/training/MobileTrainingFallbackLaunchInterstitial';
// K108-R Batch 2 — dev-only monitored-evidence readout (gated by training_debug).
import { TrainingMonitoringDebugPanel } from '@/components/training/TrainingMonitoringDebugPanel';
// K108-W — global return-to-OptiBay completion prompt. The Training tab shows a
// persistent "Record completion" CTA sourced from the app-level provider (which
// owns the auto-open + the single modal).
import { useTrainingCompletionReturnPrompt } from '@/contexts/TrainingCompletionReturnPromptProvider';
import { GraduationCap } from 'lucide-react';

// =============================================
// TYPES
// =============================================

interface TicketTrainingTabProps {
  ticket: Ticket;
  ticketJobs: Array<{ id: string; name: string; standardMinutes: number; category?: string }>;
  className?: string;
  technicianProfileId?: string | null;
  // Batch 18-K108 — when true, a monitored launch first checks that the
  // Training Companion extension is installed (tokenless PING) and, if not,
  // shows the install modal instead of launching. Default OFF: production
  // behaviour is unchanged until the PONG-capable extension (v1.2.8, the
  // current package; PONG was introduced in v1.1.0) is published to the
  // Chrome Web Store and this is turned on.
  companionInstallGate?: boolean;
}

// =============================================
// DEBUG GATE — code-level only, no production UI
// =============================================

function isDebugEnabled(): boolean {
  try {
    return typeof window !== 'undefined' && localStorage.getItem('training_debug') === 'true';
  } catch {
    return false;
  }
}

// Threshold for "Commonly used" badge
const COMMONLY_USED_THRESHOLD = 3;

// Mobile observation-unblock: detect a mobile-like runtime from INERT signals
// (touch points / viewport width) via the sanctioned F11.18 helper — NO
// userAgent sniffing. Used ONLY to decide whether a would-be-gated launch (i.e.
// Companion NOT confirmed installed) shows the unverified mobile launch dialog
// instead of the desktop install modal. An installed Companion always takes the
// monitored path regardless of runtime, so a touchscreen desktop with the
// Companion is unaffected.
function detectMobileLikeRuntimeNow(): boolean {
  if (typeof window === 'undefined') return false;
  return isMobileLikeRuntime(
    collectPassiveRuntimeSignals({
      window,
      navigator: typeof navigator !== 'undefined' ? navigator : null,
      document: typeof document !== 'undefined' ? document : null,
    }),
  );
}

// =============================================
// COMPONENT
// =============================================

export function TicketTrainingTab({ ticket, ticketJobs, className, technicianProfileId, companionInstallGate = false }: TicketTrainingTabProps) {
  const shopJobIds = useMemo(
    () => (ticketJobs ?? []).map(j => j.id),
    [ticketJobs]
  );

  const { openTraining } = useOpenTrainingWithSession();

  // Batch 18-F11.8: opt-in mobile fallback affordance, keyed by
  // artifact ID. At most one interstitial is open at a time — clicking
  // the same toggle hides it; clicking a different toggle switches.
  // Toggling is purely UI state and does NOT open the provider URL,
  // does NOT call openTraining, does NOT create a session row. Launch
  // happens only when the user clicks the interstitial's own Open
  // button, which delegates back to the same handleOpenArtifact path
  // the primary "Open" button already uses.
  const [mobileFallbackArtifactId, setMobileFallbackArtifactId] = useState<
    string | null
  >(null);
  const toggleMobileFallback = useCallback((artifactId: string) => {
    setMobileFallbackArtifactId((prev) =>
      prev === artifactId ? null : artifactId,
    );
  }, []);

  // Fetch ticket notes for Phase 2 context (not on the Ticket type prop)
  // CORE-AH-INT-005 — `isError` is destructured DELIBERATELY. The queries
  // below already `throw` on error, but reading only `data` turned a failed
  // read into `undefined`, which then became `null` in the context below —
  // UNKNOWN silently becoming KNOWN EMPTY, which this capability's
  // constitution forbids ("absent context SHALL produce an explicit
  // incomplete state"; "Unknown remains unknown").
  const { data: ticketNotes, isError: ticketNotesUnavailable } = useQuery({
    queryKey: ['ticket-notes-for-training', ticket.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tickets')
        .select('notes, customer_request_notes')
        .eq('id', ticket.id)
        .maybeSingle();
      if (error) throw error;
      return data as { notes: string | null; customer_request_notes: string | null } | null;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch workflow complaint text for DTC/keyword context
  const { data: complaintTexts, isError: complaintsUnavailable } = useQuery({
    queryKey: ['ticket-complaints-for-training', ticket.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ticket_workflow_complaints')
        .select('text')
        .eq('ticket_id', ticket.id);
      if (error) throw error;
      return (data || []).map((c: { text: string }) => c.text).filter(Boolean);
    },
    staleTime: 5 * 60 * 1000,
  });

  /**
   * CORE-AH-INT-005 — the sources whose READ FAILED, by name.
   *
   * Empty means the context is complete as far as these sources go. Non-empty
   * means the tab must say so: a recommendation may still be useful from the
   * facts that DID load, but it must not be presented as fully grounded, and
   * no reason may assert that an unread source holds nothing.
   */
  const unavailableContextSources = useMemo(() => {
    const out: string[] = [];
    if (ticketNotesUnavailable) out.push('ticket notes');
    if (complaintsUnavailable) out.push('reported complaints');
    return out;
  }, [ticketNotesUnavailable, complaintsUnavailable]);
  const contextIncomplete = unavailableContextSources.length > 0;

  // Build Phase 2 ticket context (now includes vehicle YMME + complaints)
  const ticketContext = useMemo((): TicketContext | undefined => {
    const jobNames = (ticketJobs ?? []).map(j => j.name).filter(Boolean);
    const jobCategories = (ticketJobs ?? []).map(j => j.category ?? '').filter(Boolean);
    const notes = ticketNotes?.notes ?? null;
    const customerRequestNotes = ticketNotes?.customer_request_notes ?? null;
    const complaintText = (complaintTexts ?? []).join(' ');

    // Append complaint text to notes for keyword matching
    const combinedNotes = [notes, complaintText].filter(Boolean).join(' ') || null;

    // Only provide context if we have any signal
    if (jobNames.length === 0 && jobCategories.length === 0 && !combinedNotes && !customerRequestNotes) {
      return undefined;
    }

    return {
      jobNames,
      jobCategories,
      ticketNotes: combinedNotes,
      customerRequestNotes,
      vehicleYear: ticket.vehicle?.year ?? null,
      vehicleMake: ticket.vehicle?.make ?? null,
      vehicleModel: ticket.vehicle?.model ?? null,
      vehicleEngine: (ticket.vehicle as any)?.engine_size ?? null,
    };
  }, [ticketJobs, ticketNotes, complaintTexts, ticket.vehicle]);

  // Batch 18-G8 — Pass the safe G6 options. `technicianProfileId` is
  // the acting user the parent provided; we never silently substitute
  // `ticket.assigned_technician_id`. `roleKey` / `teamId` / `domainKey`
  // are NOT passed — G5 §4 audit found them unsafe to infer. The
  // requirement query in `useJobTrainingArtifacts` simply skips when
  // `orgId` / `shopId` is absent; G8 keeps the call site minimal until
  // a future batch threads shop / org context through the parent
  // props.
  // `currentDate` feeds the recommendation query key (due-soon/overdue math).
  // Computing it inline as `clock.now().toISOString()` produced a NEW value on
  // every render, so React Query saw a new (uncached) key each render and
  // started a fresh query every time — a self-sustaining refetch loop that
  // kept `isLoading` true indefinitely (every request returned 200; the
  // spinner simply never settled). Pin it once per ticket so the key is stable
  // and the query settles to loaded / empty / error.
  const currentDate = useMemo(() => clock.now().toISOString(), [ticket.id]);
  const { data, isLoading, isError, refetch } = useJobTrainingArtifacts(shopJobIds, ticketContext, {
    technicianProfileId: technicianProfileId ?? undefined,
    ticketId: ticket.id,
    currentDate,
    limit: 25,
  });

  // Provider trust badge map (15-min cache)
  const { data: providersList } = useQuery({
    queryKey: ['ticket-training-providers-map'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('training_providers')
        .select('id, trust_badge')
        .eq('is_active', true);
      if (error) throw error;
      return data || [];
    },
    staleTime: 15 * 60 * 1000,
  });

  const providerTrustMap = useMemo(
    () => new Map((providersList || []).map((p: any) => [p.id, p.trust_badge ?? null])),
    [providersList]
  );

  const artifacts = data?.artifacts ?? [];
  const reason = data?.reason ?? null;
  const scores = data?.scores;
  const engagementMap = data?.engagementMap;
  const pensieveMap = data?.pensieveMap;
  const debugMode = isDebugEnabled();

  // K108-W: the app-level provider owns auto-open + the modal. Here we just read
  // the candidate to render a persistent "Record completion" CTA (survives a
  // dismiss); clicking it opens the provider's single modal.
  const returnPrompt = useTrainingCompletionReturnPrompt();
  const completionCandidate = returnPrompt?.candidate ?? null;
  // Batch 18-G8 — Ranked recommendations consumed via Option C
  // (ranked order with artifact lookup fallback). `artifactById` lets
  // us re-use the full `TrainingArtifact` object for card rendering
  // and the existing launch handler; `rankedByArtifactId` lets each
  // card read its per-artifact ranked metadata (badges + reason
  // codes) without changing card props. `displayArtifacts` is the
  // ordered render list: ranked entries first (skipping any whose
  // artifact id is missing — defensive against future hook drift),
  // then any artifact missing from the ranked array appended at the
  // end so we never silently drop a card. When the hook does not
  // emit a ranked array (e.g. the empty `no_vehicle` shape), the
  // existing `artifacts` ordering is preserved exactly.
  const rankedRecommendations = data?.rankedRecommendations ?? [];
  const artifactById = useMemo(
    () => new Map(artifacts.map((artifact) => [artifact.id, artifact])),
    [artifacts],
  );
  const rankedByArtifactId = useMemo(
    () =>
      new Map<string, RankedTrainingRecommendation>(
        rankedRecommendations.map((ranked) => [
          ranked.candidate.artifactId,
          ranked,
        ]),
      ),
    [rankedRecommendations],
  );
  const displayArtifacts = useMemo(() => {
    if (rankedRecommendations.length === 0) return artifacts;
    const seen = new Set<string>();
    const ordered: typeof artifacts = [];
    for (const ranked of rankedRecommendations) {
      const artifact = artifactById.get(ranked.candidate.artifactId);
      if (!artifact || seen.has(artifact.id)) continue;
      ordered.push(artifact);
      seen.add(artifact.id);
    }
    for (const artifact of artifacts) {
      if (!seen.has(artifact.id)) ordered.push(artifact);
    }
    return ordered;
  }, [artifacts, artifactById, rankedRecommendations]);

  // Batch 18-I11 — single batched provider evidence summary query
  // over the visible recommendation artifact ids. Rows are grouped by
  // `matched_artifact_id` so each JobTrainingCard receives only the
  // provider rows for its own artifact. The hook gates itself on
  // technicianProfileId + non-empty artifactIds; we mirror the gate
  // here for clarity. Provider evidence is purely additive — a
  // loading / error result never blocks the recommendation cards.
  const visibleArtifactIds = useMemo(
    () => displayArtifacts.map((a) => a.id),
    [displayArtifacts],
  );
  const providerEvidenceSummary = useTrainingProviderEvidenceSummary({
    technicianProfileId: technicianProfileId ?? null,
    artifactIds: visibleArtifactIds,
    enabled: Boolean(
      technicianProfileId && visibleArtifactIds.length > 0,
    ),
  });
  const providerRowsByArtifactId = useMemo(() => {
    const map = new Map<string, TrainingProviderEvidenceSummaryRow[]>();
    for (const row of providerEvidenceSummary.data ?? []) {
      const existing = map.get(row.matched_artifact_id);
      if (existing) {
        existing.push(row);
      } else {
        map.set(row.matched_artifact_id, [row]);
      }
    }
    return map;
  }, [providerEvidenceSummary.data]);

  // Batch 18-K108 — Companion install readiness (only pings when the gate is
  // enabled; default OFF keeps production launch behaviour unchanged).
  const companion = useCompanionReadiness({ enabled: companionInstallGate });
  const [installModalOpen, setInstallModalOpen] = useState(false);
  // Batch 18-K108 (activation fix) — when the gate is ON and the Companion IS
  // installed but the acting user has no linked technician profile, we cannot
  // mint a monitored session. We MUST NOT silently open unmonitored training;
  // we surface a blocking "profile required" dialog instead.
  const [profileRequiredOpen, setProfileRequiredOpen] = useState(false);
  // Mobile observation-unblock: unverified mobile launch dialog visibility.
  const [mobileLaunchOpen, setMobileLaunchOpen] = useState(false);
  // The artifact whose launch is deferred until the user confirms the Companion
  // is installed (via the install modal's "I installed it — check again").
  const pendingArtifactRef = useRef<TrainingArtifact | null>(null);

  // Monitored launch (mints a Companion token). Popup-safe: window.open runs
  // synchronously inside the originating click / modal-proceed gesture. Callers
  // MUST ensure technicianProfileId is present before invoking.
  const runMonitoredLaunch = useCallback((artifact: TrainingArtifact) => {
    if (!technicianProfileId || !artifact.access_url) return;
    // K108-P: delegate provider-tab opening to the Companion ONLY when the gate
    // is on, the Companion is confirmed installed, and the artifact URL is on the
    // approved provider allowlist (YouTube). Otherwise the app opens the tab
    // itself (unmonitored fallback). `approvedDomains` is defense-in-depth; the
    // Companion re-validates against its own manifest hosts.
    const approvedDomains = approvedDomainsForUrl(artifact.access_url);
    const delegateProviderTabToCompanion =
      companionInstallGate &&
      companion.state === 'installed' &&
      isApprovedProviderUrl(artifact.access_url);
    openTraining({
      technicianProfileId,
      artifactId: artifact.id,
      artifactType: artifact.artifact_type,
      expectedDurationMinutes: artifact.estimated_time_minutes,
      surface: 'ticket_sidebar',
      source: 'ticket_context',
      targetUrl: artifact.access_url,
      ticketId: ticket.id,
      jobIds: shopJobIds,
      jobCategory: (ticketJobs ?? []).find(j => j.category)?.category,
      launchSource: 'ticket_training_tab',
      delegateProviderTabToCompanion,
      approvedDomains,
    });
  }, [technicianProfileId, openTraining, ticket.id, shopJobIds, ticketJobs, companionInstallGate, companion.state]);

  // Mobile observation-unblock: open the REAL provider URL as an EXPLICITLY
  // UNVERIFIED launch. Deliberately does NOT mint a token, create a monitored
  // session, or emit Companion / completion evidence — a mobile open is never
  // treated as verified or complete. The window.open runs synchronously inside
  // the click gesture so it is not popup-blocked.
  const runUnverifiedMobileLaunch = useCallback((artifact: TrainingArtifact) => {
    if (!artifact.access_url) return;
    // Start the return-timer: persist a short-lived, client-only launch record
    // (localStorage) BEFORE opening, so that when the user returns to OptiBay we
    // can compute time-away and offer a self-attestation prompt. This is NOT
    // evidence — nothing reaches the backend until the user explicitly attests.
    writeMobileTrainingLaunch({
      artifactId: artifact.id,
      ticketId: ticket.id,
      technicianProfileId: technicianProfileId ?? null,
      providerUrl: artifact.access_url,
      durationMinutes:
        typeof artifact.estimated_time_minutes === 'number'
          ? artifact.estimated_time_minutes
          : null,
      launchedAtMs: clock.nowMs(),
    });
    window.open(artifact.access_url, '_blank', 'noopener,noreferrer');
  }, [ticket.id, technicianProfileId]);

  // Open handler — single chokepoint for every ticket training launch (primary
  // Open button AND the mobile-fallback interstitial both route here).
  const handleOpenArtifact = useCallback((artifact: TrainingArtifact) => {
    if (!artifact.access_url) return;

    // Batch 18-K108 — with the gate ON, NO ticket training artifact may open
    // until the Companion is confirmed installed. This check runs BEFORE the
    // technicianProfileId branch so a missing profile can never fall through to
    // a raw, unverified window.open.
    if (companionInstallGate) {
      // 1. Companion not confirmed installed (unknown/missing) → the URL never
      //    opens through the monitored path. Defer this artifact until either a
      //    PONG confirms install (desktop) or the user opts into an unverified
      //    mobile launch.
      if (shouldGateMonitoredLaunch(companion.state)) {
        pendingArtifactRef.current = artifact;
        // 1a. Mobile-like runtime — the Companion extension cannot install here,
        //     so the desktop install modal is a dead-end. Show the unverified
        //     mobile launch dialog instead. Reached ONLY when the Companion is
        //     not confirmed installed; an installed Companion took the monitored
        //     path, so a touchscreen desktop with the Companion is unaffected.
        if (detectMobileLikeRuntimeNow()) {
          setMobileLaunchOpen(true);
          return;
        }
        // 1b. Desktop without a confirmed Companion → install modal (unchanged).
        setInstallModalOpen(true);
        return;
      }
      // 2. Companion installed, but no linked technician profile → cannot mint a
      //    monitored session. Block with a clear message; never open unmonitored.
      if (!technicianProfileId) {
        setProfileRequiredOpen(true);
        return;
      }
      // 3. Companion installed + linked profile → monitored launch.
      runMonitoredLaunch(artifact);
      return;
    }

    // Gate OFF — legacy behaviour preserved exactly: monitored launch when a
    // technician profile exists, else best-effort raw open.
    if (technicianProfileId) {
      runMonitoredLaunch(artifact);
    } else {
      window.open(artifact.access_url, '_blank', 'noopener,noreferrer');
    }
  }, [companionInstallGate, companion.state, technicianProfileId, runMonitoredLaunch]);

  return (
    <ScrollArea className={cn('h-[calc(85vh-160px)]', className)}>
      {companionInstallGate && (
        <>
          <CompanionInstallModal
            open={installModalOpen}
            onClose={() => setInstallModalOpen(false)}
            onProceed={() => {
              // Invoked only after the modal's own PONG re-check confirms the
              // Companion is installed. If the acting user still has no linked
              // technician profile, we cannot mint a monitored session — block
              // instead of opening unmonitored training.
              const artifact = pendingArtifactRef.current;
              pendingArtifactRef.current = null;
              setInstallModalOpen(false);
              if (!artifact) return;
              if (technicianProfileId) {
                runMonitoredLaunch(artifact);
              } else {
                setProfileRequiredOpen(true);
              }
            }}
          />
          <TrainingProfileRequiredDialog
            open={profileRequiredOpen}
            onClose={() => setProfileRequiredOpen(false)}
          />
          <MobileTrainingLaunchDialog
            open={mobileLaunchOpen}
            onClose={() => {
              pendingArtifactRef.current = null;
              setMobileLaunchOpen(false);
            }}
            onOpenTraining={() => {
              // Unverified mobile launch: open the real URL (no monitored
              // session, no token, no evidence), then close. window.open runs
              // synchronously within this click gesture.
              const artifact = pendingArtifactRef.current;
              pendingArtifactRef.current = null;
              if (artifact) runUnverifiedMobileLaunch(artifact);
              setMobileLaunchOpen(false);
            }}
          />
        </>
      )}
      <div className="px-6 pb-6 space-y-4 pt-4">
        {/* K108-W: non-blocking CTA to attest a monitored session left pending.
            Stays visible after dismiss; the app-level provider auto-opens the modal
            once when the user returns to OptiBay. */}
        {completionCandidate && (
          <div
            data-testid="ticket-record-completion-cta"
            className="flex items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 p-3"
          >
            <div className="flex items-center gap-2 min-w-0">
              <GraduationCap className="h-4 w-4 flex-shrink-0 text-primary" />
              <span className="text-sm truncate">
                Record training completion for{' '}
                <span className="font-medium">{completionCandidate.session.artifact.title}</span>
              </span>
            </div>
            <Button size="sm" onClick={() => returnPrompt?.openPrompt()} className="flex-shrink-0">
              Record completion
            </Button>
          </div>
        )}

        {/* Primary section: Job-based recommendations */}
        <div>
          <h3 className="text-sm font-semibold mb-3">Recommended Training for This Ticket</h3>

          {/* K108-R Batch 2 — DEV-only monitored-evidence readout (renders null
              unless localStorage training_debug === 'true'). */}
          {debugMode && (
            <TrainingMonitoringDebugPanel technicianProfileId={technicianProfileId} />
          )}

          {/* CORE-AH-INT-005 — incomplete context is DISCLOSED, not hidden, and
              it does not blank the tab. Recommendations continue from the facts
              that did load, because this capability must "remain useful on Core
              facts alone" — but the reader is told what could not be read, so a
              thinner list is never mistaken for a fully grounded one. */}
          {!isLoading && contextIncomplete && (
            <div
              data-testid="training-context-incomplete"
              role="status"
              className="mb-3 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/40 dark:text-amber-200"
            >
              <span className="font-medium">Incomplete context.</span>{' '}
              Couldn’t read {unavailableContextSources.join(' and ')} for this ticket, so these
              recommendations may be missing relevant signals. This is a read failure —
              <span className="font-medium"> not</span> a finding that the ticket has none.
            </div>
          )}

          {isLoading && (
            <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading recommendations…
            </div>
          )}

          {!isLoading && isError && (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground gap-3">
              <span className="text-sm">Couldn’t load training recommendations.</span>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          )}

          {!isLoading && !isError && artifacts.length === 0 && (
            <EmptyState reason={reason} />
          )}

          {!isLoading && !isError && artifacts.length > 0 && (
            <div className="space-y-2">
              {displayArtifacts.map(artifact => {
                // Batch 18-B: the breakdown is always passed so the
                // reason helper can compute a render-only explanation.
                // The numeric debug line below `scoreBreakdownDebug` is
                // still gated on `debugMode` exactly as before.
                const breakdown = scores?.get(artifact.id);
                // Batch 18-G8 — Per-artifact ranked metadata from the
                // G6 hook. `undefined` when the artifact did not make
                // it into the ranked array (e.g. the hook returned
                // `artifacts` without `rankedRecommendations`); the
                // card renders its current layout in that case.
                const ranked = rankedByArtifactId.get(artifact.id);
                // Batch 18-I11 — per-card provider rows looked up
                // from the parent's grouped map. Empty array when
                // the artifact has no provider evidence; the helper
                // then yields zero badges and the group is hidden.
                const providerRowsForCard =
                  providerRowsByArtifactId.get(artifact.id) ?? [];
                return (
                  <JobTrainingCard
                    key={artifact.id}
                    artifact={artifact}
                    providerTrustMap={providerTrustMap}
                    scoreBreakdown={breakdown}
                    scoreBreakdownDebug={debugMode ? breakdown : undefined}
                    engagementStats={engagementMap?.get(artifact.id)}
                    pensieveStats={pensieveMap?.get(artifact.id)}
                    ranked={ranked}
                    providerEvidenceRows={providerRowsForCard}
                    onOpen={handleOpenArtifact}
                    isMobileFallbackOpen={
                      mobileFallbackArtifactId === artifact.id
                    }
                    onToggleMobileFallback={() =>
                      toggleMobileFallback(artifact.id)
                    }
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* CDR-2.1: gated behind learningCenter_intelligence (Catalyst+ preset only)
            so Core users do not see "Coming Soon" copy in a Core demo path.
            Catalyst+ users still see the future-mode roadmap. */}
        <RequireFeature feature="learningCenter_intelligence">
          <Collapsible>
            <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg bg-muted/30 hover:bg-muted/40 transition-colors group">
              <div className="flex items-center gap-2 text-muted-foreground">
                <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
                <span className="text-sm font-medium">More Recommendations</span>
                <Badge variant="outline" className="text-xs">Coming Soon</Badge>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2 px-3 pb-2">
              <div className="space-y-2 text-xs text-muted-foreground">
                <div className="flex items-center gap-2">✅ Vehicle YMME fitment matching</div>
                <div className="flex items-center gap-2">✅ Complaint / DTC keyword matching</div>
                <div className="flex items-center gap-2">✅ Engagement & behavioral scoring</div>
                <div className="flex items-center gap-2"><Lock className="h-3 w-3" /> Capability-based matching</div>
                <div className="flex items-center gap-2"><Lock className="h-3 w-3" /> Cross-vehicle context matching</div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </RequireFeature>
      </div>
    </ScrollArea>
  );
}

// =============================================
// SUB-COMPONENTS
// =============================================

/**
 * Batch 18-K108 (activation fix) — blocking dialog shown when the Companion
 * install gate is ON and the Companion is installed, but the acting user has
 * no linked technician profile. Monitored training cannot be launched without
 * a profile to mint a session against, and the gate forbids opening unmonitored
 * training, so we surface this instead of silently opening the URL.
 */
function TrainingProfileRequiredDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent data-testid="training-profile-required-dialog">
        <DialogHeader>
          <DialogTitle>Technician profile required</DialogTitle>
          <DialogDescription>
            Training monitoring requires a linked technician profile for this
            user. Ask an administrator to link your account to a technician
            profile, then try launching the training again.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmptyState({ reason }: { reason?: string | null }) {
  const messages: Record<string, string> = {
    no_jobs: 'No jobs on this ticket.',
    no_catalog_match: 'Job type not yet linked to training catalog.',
    no_training_mapped: 'No training mapped to this job yet.',
    no_relevant_match: 'No training matches this ticket yet.',
    no_vehicle: 'Vehicle year, make, model, and engine are required for training recommendations.',
  };

  return (
    <div className="flex items-center gap-2 py-4 px-3 text-sm text-muted-foreground">
      <SearchX className="h-4 w-4 shrink-0" />
      {messages[reason || 'no_training_mapped'] || messages.no_training_mapped}
    </div>
  );
}

/** Validates that a URL starts with http:// or https:// */
function isValidHttpUrl(url: string | null | undefined): url is string {
  if (!url) return false;
  return url.startsWith('http://') || url.startsWith('https://');
}

/** Format minutes as readable duration */
function formatDuration(minutes: number | null | undefined): string | null {
  if (!minutes || minutes <= 0) return null;
  if (minutes < 60) return `~${minutes} min`;
  return `~${Math.round(minutes / 60 * 10) / 10} hrs`;
}

// =====================================================================
// Batch 18-G8 — Ranked-policy badge + reason helpers
// =====================================================================
// Tiny pure helpers; both return narrow shapes the card can render
// without any extra hooks. The card calls these once per render and
// falls back to its existing layout when `ranked` is undefined.
//
// Badge rules (G7 §7):
//   - `Required by shop` and `Manager verification required` are
//     policy badges, not confidence / certification badges.
//   - `Overdue` suppresses `Due soon`.
//   - Evidence badges (`Verified`, `Revoked`, `Verification pending`,
//     `Previously completed`) are deferred to a later batch. G8
//     explicitly does not render `Verified` as confidence and does
//     not render `Revoked` as completed.
//
// Reason fragment rules (G7 §8):
//   - Returns `null` for non-required candidates so the existing
//     score-breakdown explanation continues to drive the card's
//     "Recommended because: …" line. As of CORE-AH that explanation is
//     `buildTrainingRecommendationExplanation(scoreBreakdown)`, which
//     emits the Core line AND a separate learned-enrichment line; the
//     policy reason is independent of both.
//   - Reason labels never say `certified`, `verified` (as confidence),
//     `completed` (as recommendation reason), or `confidence`.

interface RankedPolicyBadge {
  label: string;
  testId: string;
  variant: 'default' | 'secondary' | 'outline' | 'destructive';
}

function getRankedPolicyBadges(
  ranked: RankedTrainingRecommendation | undefined,
): RankedPolicyBadge[] {
  if (!ranked) return [];
  const codes = new Set(ranked.reasons.map((reason) => reason.code));
  const badges: RankedPolicyBadge[] = [];
  if (ranked.badges.required) {
    badges.push({
      label: 'Required by shop',
      testId: 'ranked-badge-required-by-shop',
      variant: 'default',
    });
  }
  if (ranked.badges.managerVerificationRequired) {
    badges.push({
      label: 'Manager verification required',
      testId: 'ranked-badge-manager-verification-required',
      variant: 'outline',
    });
  }
  // Overdue suppresses Due soon — the ranker already enforces
  // precedence in `reasons`, this is belt-and-suspenders.
  if (codes.has('overdue')) {
    badges.push({
      label: 'Overdue',
      testId: 'ranked-badge-overdue',
      variant: 'destructive',
    });
  } else if (codes.has('due_soon')) {
    badges.push({
      label: 'Due soon',
      testId: 'ranked-badge-due-soon',
      variant: 'secondary',
    });
  }
  return badges;
}

function buildRankedReasonFragment(
  ranked: RankedTrainingRecommendation | undefined,
): string | null {
  if (!ranked) return null;
  const codes = new Set(ranked.reasons.map((reason) => reason.code));
  if (!codes.has('required_by_shop')) return null;
  if (codes.has('overdue')) return 'Required by shop policy. Overdue.';
  if (codes.has('due_soon')) return 'Required by shop policy. Due soon.';
  if (codes.has('manager_verification_required')) {
    return 'Required by shop policy. Manager verification required.';
  }
  return 'Required by shop policy.';
}

function JobTrainingCard({
  artifact,
  providerTrustMap,
  scoreBreakdown,
  scoreBreakdownDebug,
  engagementStats,
  pensieveStats,
  ranked,
  providerEvidenceRows,
  onOpen,
  isMobileFallbackOpen,
  onToggleMobileFallback,
}: {
  artifact: TrainingArtifact;
  providerTrustMap: Map<string, string | null>;
  /** Batch 18-B: used to derive a render-only English reason fragment. */
  scoreBreakdown?: ScoreBreakdown;
  /** Pre-Batch-18-B numeric debug line; gated on localStorage `training_debug`. */
  scoreBreakdownDebug?: ScoreBreakdown;
  engagementStats?: EngagementStats;
  pensieveStats?: PensieveStats;
  /** Batch 18-G8 — per-artifact ranked metadata (badges + reason
   *  codes) from the G6 data bridge. `undefined` when the artifact
   *  is not in the ranked array. */
  ranked?: RankedTrainingRecommendation;
  /** Batch 18-I11 — provider evidence rows for this card's artifact,
   *  pre-sliced by the parent from `providerRowsByArtifactId`. Always
   *  defined (empty array when no provider evidence exists). */
  providerEvidenceRows: TrainingProviderEvidenceSummaryRow[];
  onOpen: (artifact: TrainingArtifact) => void;
  /** Batch 18-F11.8: whether THIS card's mobile-fallback interstitial
   *  is currently expanded. At most one is open across the list. */
  isMobileFallbackOpen: boolean;
  /** Batch 18-F11.8: toggles this card's mobile-fallback interstitial. */
  onToggleMobileFallback: () => void;
}) {
  const hasValidUrl = isValidHttpUrl(artifact.access_url);
  const duration = formatDuration(artifact.estimated_time_minutes);
  const providerId = (artifact as any).provider_id as string | undefined;
  const isCommonlyUsed = (engagementStats?.uniqueOpeners ?? 0) >= COMMONLY_USED_THRESHOLD;
  const showPensieveBadge = hasPensieveBadge(pensieveStats);
  // Batch 18-B: compose the operator-facing "Recommended because: …"
  // fragment. The helper is pure and consults only fields already
  // present in the existing `ScoreBreakdown`. No DB call, no ranking
  // change.
  // CORE-AH: the displayed basis, separated by who owns it. Built from the
  // SAME `scoreBreakdown` this card is rendering and that the ranking used —
  // not a second lookup — so the explanation cannot drift from the result it
  // explains. Still pure, still ranking-neutral.
  const explanation = buildTrainingRecommendationExplanation(scoreBreakdown);
  // Batch 18-G8 — ranked policy badges + reason fragment. Both
  // helpers return narrow shapes / null when `ranked` is absent.
  const policyBadges = getRankedPolicyBadges(ranked);
  const rankedReasonFragment = buildRankedReasonFragment(ranked);
  // Batch 18-H2 — ticket-specific evidence display state. Reads only
  // `ranked.candidate.completionSummary` (already attached by the G6
  // bridge); no new query, no F36 composition. Helper returns the
  // empty display state when `ranked` is absent or status is `none`.
  const evidenceDisplayState = buildTicketTrainingEvidenceDisplayState(
    ranked?.candidate.completionSummary,
  );
  // Batch 18-I11 — provider evidence display state at the 'ticket'
  // surface. The I8 helper hides revoked / pending / superseded / none
  // on this surface; only attested produces a badge. Provider state
  // NEVER feeds `buildTicketTrainingEvidenceDisplayState` above, so
  // H2 manager/manual labels (Verified by manager, Verification
  // pending, Verification revoked, Manual completion submitted) and
  // `suppressCompleteDisplay` semantics remain unchanged.
  const providerEvidenceDisplay = buildProviderEvidenceDisplayState(
    providerEvidenceRows,
    'ticket',
  );
  const providerRowByEventId = new Map(
    providerEvidenceRows.map(
      (row) => [row.latest_event_id, row] as const,
    ),
  );

  return (
    <div className="p-3 rounded-lg border bg-card hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-lg shrink-0">
              {ARTIFACT_TYPE_ICONS[artifact.artifact_type as keyof typeof ARTIFACT_TYPE_ICONS] || '📄'}
            </span>
            <h4 className="text-sm font-medium truncate">{artifact.title}</h4>
          </div>
          {artifact.provider_name && (
            <div className="flex items-center gap-1.5 mt-1 pl-7">
              <p className="text-xs text-muted-foreground">{artifact.provider_name}</p>
              <ProviderTrustBadge trustBadge={providerTrustMap?.get(providerId ?? '') ?? null} />
              {isCommonlyUsed && (
                <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground/70">
                  <Users className="h-2.5 w-2.5" />
                  Commonly used
                </span>
              )}
              {showPensieveBadge && (
                <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground/70">
                  ✦ Supports diagnostic workflow
                </span>
              )}
            </div>
          )}
          {/* Batch 18-G8 — policy badges (Required by shop, Manager
              verification required, Overdue/Due soon). Rendered above
              the metadata badge row so they read as a separate
              policy / recommendation family. Empty render when
              `policyBadges` is empty (no ranked metadata or no
              policy tier 1 candidate). */}
          {policyBadges.length > 0 && (
            <div
              className="flex flex-wrap gap-1 mt-1.5 pl-7"
              data-testid="training-card-ranked-policy-badges"
            >
              {policyBadges.map((badge) => (
                <Badge
                  key={badge.testId}
                  variant={badge.variant}
                  className="text-xs"
                  data-testid={badge.testId}
                >
                  {badge.label}
                </Badge>
              ))}
            </div>
          )}
          {/* Batch 18-H2 — evidence badges (Verified by manager,
              Verification pending, Verification revoked, Manual
              completion submitted). Rendered as a sibling row below
              the policy-badges row so the two families read as
              distinct: policy = "what the shop requires", evidence =
              "what this technician's record shows". Empty render
              when `evidenceDisplayState.evidenceBadges` is empty
              (no ranked metadata, or `completionSummary.status` is
              `none` / undefined). */}
          {evidenceDisplayState.evidenceBadges.length > 0 && (
            <div
              className="flex flex-wrap gap-1 mt-1.5 pl-7"
              data-testid="training-card-ticket-evidence-badges"
            >
              {evidenceDisplayState.evidenceBadges.map((badge) => (
                <Badge
                  key={badge.testId}
                  variant={badge.variant}
                  className="text-xs"
                  data-testid={badge.testId}
                >
                  {badge.label}
                </Badge>
              ))}
            </div>
          )}
          {/* Batch 18-I11 — provider evidence badge family. Sibling
              to the G8 ranked-policy row and the H2 ticket-evidence
              row above, NEVER merged into either container. The I8
              helper at surface 'ticket' emits a badge only for
              attested; revoked / pending / superseded / none never
              render here. Provider attestation NEVER replaces H2
              manager/manual labels or `suppressCompleteDisplay`. */}
          {providerEvidenceDisplay.evidenceBadges.length > 0 && (
            <div
              className="flex flex-wrap gap-1 mt-1.5 pl-7"
              data-testid="training-card-provider-evidence-badges"
            >
              {providerEvidenceDisplay.evidenceBadges.map((badge) => {
                const row = badge.latestEventId
                  ? providerRowByEventId.get(badge.latestEventId)
                  : undefined;
                return (
                  <span
                    key={badge.latestEventId ?? badge.testId}
                    className="inline-flex items-center gap-1"
                  >
                    {(badge.providerDisplayName || row?.provider_key) && (
                      <span
                        className="text-[10px] text-muted-foreground"
                        data-testid="training-card-provider-evidence-provider-name"
                      >
                        {badge.providerDisplayName ?? row?.provider_key}
                      </span>
                    )}
                    <Badge
                      variant={badge.variant}
                      className="text-xs"
                      data-testid={badge.testId}
                    >
                      {badge.label}
                    </Badge>
                  </span>
                );
              })}
            </div>
          )}
          <div className="flex flex-wrap gap-1 mt-1.5 pl-7">
            <Badge variant="secondary" className="text-xs">
              {ARTIFACT_TYPE_LABELS[artifact.artifact_type as keyof typeof ARTIFACT_TYPE_LABELS] || artifact.artifact_type}
            </Badge>
            {artifact.skill_depth && (
              <Badge variant="outline" className="text-xs">
                {SKILL_DEPTH_LABELS[artifact.skill_depth as keyof typeof SKILL_DEPTH_LABELS] || artifact.skill_depth}
              </Badge>
            )}
            {artifact.access_type && (
              <Badge
                variant={artifact.access_type === 'free' ? 'default' : 'outline'}
                className="text-xs"
              >
                {ACCESS_TYPE_LABELS[artifact.access_type as keyof typeof ACCESS_TYPE_LABELS] || artifact.access_type}
              </Badge>
            )}
          </div>
          {duration && (
            <p className="text-xs text-muted-foreground mt-1.5 pl-7 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {duration}
            </p>
          )}
          {/* Batch 18-G8 — required-policy reason fragment. Only
              renders when the artifact has tier-1 `required_by_shop`
              reason codes; otherwise omitted so the existing
              `reasonFragment` line below drives the copy. */}
          {rankedReasonFragment && (
            <p
              className="text-xs text-muted-foreground mt-1.5 pl-7"
              data-testid="training-card-ranked-policy-reason"
            >
              {rankedReasonFragment}
            </p>
          )}
          {/*
            CORE-AH — the displayed basis, in two classes that are rendered
            independently.

            The Core line carries OptiBay's own basis: ticket/job and vehicle
            matches, plus Core-owned derivations over OptiBay's own
            interaction data. It keeps the two-reason cap.

            The learned line carries Catalyst inference and is rendered on its
            OWN condition. That independence is the fix: the Core cap can no
            longer push a learned contribution off the end of a shared list
            and leave the artifact explaining itself entirely in Core terms.

            A learned-only recommendation has no Core line at all, so nothing
            learned can read as an OptiBay claim about this ticket.
          */}
          {explanation.coreFragment && (
            <p
              className="text-xs text-muted-foreground mt-1.5 pl-7"
              data-testid="training-card-recommendation-reason"
              data-basis="core"
            >
              <span className="font-medium">{RECOMMENDATION_REASON_PREFIX}</span>{' '}
              {explanation.coreFragment}
            </p>
          )}
          {explanation.learnedFragment && (
            <p
              className="text-xs text-muted-foreground/90 mt-1 pl-7 italic"
              data-testid="training-card-learned-enrichment"
              data-basis="learned"
            >
              <span className="font-medium not-italic">{LEARNED_ENRICHMENT_LABEL}</span>{' '}
              {explanation.learnedFragment}
            </p>
          )}
          {/* Phase 4 debug: extended score breakdown with Pensieve (dev-only, gated by localStorage) */}
          {scoreBreakdownDebug && (
            <p className="text-[10px] text-muted-foreground/60 mt-1 pl-7 font-mono">
              Score: {scoreBreakdownDebug.total} (cat:{scoreBreakdownDebug.category} kw:{scoreBreakdownDebug.keyword} eng:{scoreBreakdownDebug.engagement} pen:{scoreBreakdownDebug.pensieve} imp:{scoreBreakdownDebug.impact} fit:{scoreBreakdownDebug.fitment} sem:{scoreBreakdownDebug.semantic})
            </p>
          )}
        </div>

        <div className="shrink-0">
          {hasValidUrl ? (
            <Button
              size="sm"
              variant="outline"
              className="gap-1"
              onClick={() => onOpen(artifact)}
              data-testid={`ticket-training-open-${artifact.id}`}
            >
              Open <ExternalLink className="h-3 w-3" />
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground italic">Link unavailable</span>
          )}
        </div>
      </div>

      {/* Batch 18-F11.8: opt-in mobile fallback affordance.
          Only shown when the primary URL is valid (same gate as the
          Open button above). The toggle button is a ghost-variant
          secondary; clicking it never auto-opens the provider URL
          and never calls openTraining. The launch only happens
          after the user clicks the interstitial's own Open button,
          which delegates back to onOpen(artifact) — the exact same
          path the primary "Open" button uses. */}
      {hasValidUrl && (
        <div className="mt-2 pl-7 space-y-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-xs"
            onClick={onToggleMobileFallback}
            data-testid={`ticket-training-mobile-fallback-toggle-${artifact.id}`}
          >
            {isMobileFallbackOpen
              ? 'Hide mobile fallback'
              : 'Open with mobile fallback'}
          </Button>
          {isMobileFallbackOpen && (
            <MobileTrainingFallbackLaunchInterstitial
              trainingTitle={artifact.title}
              providerName={artifact.provider_name}
              providerUrl={artifact.access_url ?? ''}
              estimatedDurationMinutes={
                artifact.estimated_time_minutes ?? null
              }
              onOpenProvider={() => onOpen(artifact)}
              onCancel={onToggleMobileFallback}
            />
          )}
        </div>
      )}
    </div>
  );
}
